import { TrackManagerTrack } from "@/types/shared";
import { DropdownMenu, IconButton, Text } from "@radix-ui/themes";
import {
  AccessorFnColumnDef,
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";
import { EllipsisVertical, MapPin, Pencil } from "lucide-react";
import BasicTable from "../BasicTable";

interface Props {
  tracks: TrackManagerTrack[];
  setSelectedRow: React.Dispatch<
    React.SetStateAction<TrackManagerTrack | null>
  >;
}

export default function TracksTable({ tracks, setSelectedRow }: Props) {
  const [data, setData] = useState(() => [...tracks]);

  useEffect(() => {
    setData([...tracks]);
  }, [tracks]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const columnHelper = createColumnHelper<TrackManagerTrack>();

  const columns = useMemo<ColumnDef<TrackManagerTrack, any>[]>(
    () => [
      columnHelper.accessor("name", {
        header: "Track",
        meta: { filterVariant: "text" },
        cell: (props) => (
          <Text weight="medium">{props.row.original.name}</Text>
        ),
      } as AccessorFnColumnDef<TrackManagerTrack, any>),
      columnHelper.accessor("city", {
        header: "Location",
        accessorFn: (row) => `${row.city}, ${row.state}`,
        meta: { filterVariant: "text" },
        cell: (props) => (
          <Text color="gray">
            {props.row.original.city}, {props.row.original.state}
          </Text>
        ),
      } as AccessorFnColumnDef<TrackManagerTrack, any>),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: (props) => (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <IconButton
                variant="ghost"
                size="1"
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisVertical size="16px" />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <DropdownMenu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = props.row.original.edit_path;
                }}
              >
                <Pencil size="14px" />
                Edit Venue Info
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `/tracks/${props.row.original.slug}`;
                }}
              >
                <MapPin size="14px" />
                View Track
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <BasicTable
      table={table}
      handleRowOnClick={(row) => setSelectedRow(row.original)}
    />
  );
}
